import React, { useState, useEffect, useCallback } from 'react';
import './TeamPoints.css';
import Statistics from './Statistics';
import DailyActivity from './DailyActivity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const toLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function fmtRange(startStr, endStr) {
  if (!startStr || !endStr) return '';
  const opts = { month: 'short', day: 'numeric' };
  // parse as local date (avoid UTC shift)
  const p = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
  const s = p(startStr), e = p(endStr);
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

const TeamPoints = ({ onBack, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedDirectorId, setSelectedDirectorId] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [dailyViewManager, setDailyViewManager] = useState(null);
  const [search, setSearch] = useState('');
  const [managerDate, setManagerDate] = useState(() => toLocalISO(new Date()));
  const [managerDailyPts, setManagerDailyPts] = useState({});
  const [dailyLoading, setDailyLoading] = useState(false);

  const headers = { 'X-User-ID': String(user.userId) };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/team/points-overview/`, { headers });
      if (!res.ok) throw new Error('Failed to load');
      setData(await res.json());
    } catch {
      setError('Failed to load team points.');
    } finally {
      setLoading(false);
    }
  }, [user.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const role = data?.role;
    if (!role || (role !== 'senior' && role !== 'director')) return;
    if (selectedManagerId) return;
    if (role === 'senior' && !selectedDirectorId) return;
    setDailyLoading(true);
    let url = `${API_URL}/api/team/manager-daily/?date=${managerDate}`;
    if (role === 'senior') url += `&director_id=${selectedDirectorId}`;
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(json => setManagerDailyPts(json.daily || {}))
      .catch(() => setManagerDailyPts({}))
      .finally(() => setDailyLoading(false));
  }, [data, managerDate, selectedDirectorId, selectedManagerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftManagerDay = (delta) => {
    const [y, m, d] = managerDate.split('-').map(Number);
    setManagerDate(toLocalISO(new Date(y, m - 1, d + delta)));
  };

  const nameOf = (n) => n.full_name || n.username;

  // Rep leaf → individual statistics
  if (selectedRep) {
    return (
      <Statistics
        onBack={() => setSelectedRep(null)}
        user={user}
        forUserId={selectedRep.id}
        adminTitle={`Statistics for ${nameOf(selectedRep)}`}
      />
    );
  }

  // Manager daily view → show that manager's team's daily activity
  if (dailyViewManager) {
    return (
      <DailyActivity
        onBack={() => setDailyViewManager(null)}
        user={user}
        managerId={dailyViewManager.id}
        managerName={nameOf(dailyViewManager)}
      />
    );
  }

  const role = data?.role;
  const directors = role === 'senior' ? (data.directors || []) : [];
  const selectedDirector = directors.find((d) => d.id === selectedDirectorId) || null;

  let managers = [];
  if (role === 'senior') managers = selectedDirector ? selectedDirector.managers : [];
  else if (role === 'director') managers = data.managers || [];
  const selectedManager = managers.find((m) => m.id === selectedManagerId) || null;

  let reps = [];
  if (role === 'manager' || role === 'trainer') reps = data ? data.reps || [] : [];
  else if (selectedManager) reps = selectedManager.reps || [];

  const renderBreadcrumb = () => {
    const crumbs = [];
    if (role === 'senior') {
      crumbs.push({ label: 'Directors', onClick: () => { setSelectedDirectorId(null); setSelectedManagerId(null); } });
      if (selectedDirector) crumbs.push({ label: nameOf(selectedDirector), onClick: () => setSelectedManagerId(null) });
    } else if (role === 'director') {
      crumbs.push({ label: 'My Managers', onClick: () => setSelectedManagerId(null) });
    } else if (role === 'trainer') {
      crumbs.push({ label: 'All Reps', onClick: null });
    } else {
      crumbs.push({ label: 'My Team', onClick: null });
    }
    if (selectedManager) crumbs.push({ label: nameOf(selectedManager), onClick: null });
    return (
      <div className="tp-breadcrumb">
        {crumbs.map((c, i) => (
          <span key={i} className="tp-crumb">
            {i > 0 && <span className="tp-crumb-sep">›</span>}
            {c.onClick
              ? <button className="tp-crumb-link" onClick={c.onClick}>{c.label}</button>
              : <span className="tp-crumb-current">{c.label}</span>}
          </span>
        ))}
      </div>
    );
  };

  const nodeCard = (n, sub, onClick) => (
    <button key={n.id} className="tp-node-card" onClick={onClick}>
      <div className="tp-node-info">
        <span className="tp-node-name">{nameOf(n)}</span>
        <span className="tp-node-sub">{sub}</span>
      </div>
      <div className="tp-node-right">
        <span className="tp-points">{n.points} pts</span>
        <span className="tp-chevron">›</span>
      </div>
    </button>
  );

  const renderBody = () => {
    if (loading) return <div className="tp-loading">Loading...</div>;
    if (error) return <div className="tp-error">{error}</div>;
    if (!role) return null;

    // pick the active list by drill level
    if (role === 'senior' && !selectedDirector) {
      if (!directors.length) return <div className="tp-empty">No directors found.</div>;
      return <div className="tp-cards">{directors.map((d) =>
        nodeCard(d, `${d.manager_count} manager${d.manager_count !== 1 ? 's' : ''}`,
          () => { setSelectedDirectorId(d.id); setSelectedManagerId(null); }))}</div>;
    }
    if ((role === 'senior' || role === 'director') && !selectedManager) {
      if (!managers.length) return <div className="tp-empty">No managers in this team.</div>;
      const todayISO = toLocalISO(new Date());
      return (
        <>
          <div className="tp-date-nav">
            <button className="tp-nav-btn" onClick={() => shiftManagerDay(-1)}>◀</button>
            <input
              type="date"
              className="tp-date-input"
              value={managerDate}
              max={todayISO}
              onChange={(e) => e.target.value && setManagerDate(e.target.value)}
            />
            <button className="tp-nav-btn" onClick={() => shiftManagerDay(1)} disabled={managerDate === todayISO}>▶</button>
          </div>
          <div className="tp-cards">
            {managers.map((m) => (
              <div key={m.id} className="tp-manager-row">
                <button className="tp-node-card tp-node-card--flex" onClick={() => setSelectedManagerId(m.id)}>
                  <div className="tp-node-info">
                    <span className="tp-node-name">{nameOf(m)}</span>
                    <span className="tp-node-sub">{m.rep_count} rep{m.rep_count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="tp-node-right">
                    <span className="tp-points">
                      {dailyLoading ? '…' : (managerDailyPts[String(m.id)] ?? 0)} pts
                    </span>
                    <span className="tp-chevron">›</span>
                  </div>
                </button>
                <button className="tp-daily-btn" onClick={() => setDailyViewManager(m)}>
                  Daily View
                </button>
              </div>
            ))}
          </div>
        </>
      );
    }
    // reps
    if (!reps.length) return <div className="tp-empty">No reps in this team.</div>;

    // Trainer sees every rep — add a search box and show the owning manager.
    if (role === 'trainer') {
      const q = search.trim().toLowerCase();
      const filtered = q
        ? reps.filter((r) =>
            (r.full_name || '').toLowerCase().includes(q) ||
            (r.username || '').toLowerCase().includes(q) ||
            (r.owner || '').toLowerCase().includes(q))
        : reps;
      const sub = (r) => r.owner ? `@${r.username} · ${r.owner}` : `@${r.username} · Unclaimed`;
      return (
        <>
          <input
            className="tp-search"
            type="text"
            placeholder="Search reps by name, username, or manager..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="tp-count">{filtered.length} of {reps.length} reps</div>
          {filtered.length === 0
            ? <div className="tp-empty">No reps match "{search}".</div>
            : <div className="tp-cards">{filtered.map((r) =>
                nodeCard(r, sub(r), () => setSelectedRep(r)))}</div>}
        </>
      );
    }

    return <div className="tp-cards">{reps.map((r) =>
      nodeCard(r, `@${r.username}`, () => setSelectedRep(r)))}</div>;
  };

  return (
    <div className="tp-container">
      <div className="tp-header">
        <button className="tp-back-btn" onClick={onBack}>Back</button>
        <h2>Team Points</h2>
      </div>

      {data && (
        <div className="tp-fiscal">Fiscal month: {fmtRange(data.fiscal_start, data.fiscal_end)}</div>
      )}

      {renderBreadcrumb()}
      {renderBody()}
    </div>
  );
};

export default TeamPoints;
